/**
 * @fileoverview Super-admin analytics console (PR A4).
 *
 * Consumes the four read endpoints shipped in PR A3:
 *   GET /api/{secret}/admin/analytics/now
 *   GET /api/{secret}/admin/analytics/trends
 *   GET /api/{secret}/admin/analytics/people
 *   GET /api/{secret}/admin/analytics/people/<id>
 *   GET /api/{secret}/admin/analytics/sessions
 *   GET /api/{secret}/admin/analytics/sessions/<uuid>
 *
 * Four views behind a segmented control:
 *   - Now      — active session count, engaged seconds, top pages / countries /
 *                devices / browsers over a 5–60min window.
 *   - Trends   — daily series (sessions, pageviews, engaged seconds, unique
 *                users, bounce rate) over 1–365 days.
 *   - People   — identified users, ordered by last-seen; click a row to see
 *                profile + recent sessions/events.
 *   - Sessions — every visit including anonymous ones. People can only ever
 *                show signed-in users (it JOINs `users`), which made the
 *                console look empty while real traffic was being recorded.
 *
 * Both People and Sessions drill into the same `SessionDetail` panel: an
 * inline expanding row with a device/geo facts grid plus the ordered event
 * breadcrumb for that visit (GET .../analytics/sessions/<uuid>).
 *
 * Design invariants (see plan.md PR A4):
 *   - All numeric/money values render in `var(--font-m)` (mono).
 *   - Loading states show the seedling loader; errors surface an inline
 *     retry — never a bare "network error" toast.
 *   - Every SVG uses `aria-hidden="true"` — the numbers are what a screen
 *     reader announces, not the geometry.
 *   - Focus management: switching views moves focus to the view heading.
 *   - Responsive to 640px — top tallies stack vertically below 900px.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Loading from '../components/Loading';

// ---------------------------------------------------------------------------
// Small formatting helpers — mono values only via className, not inline style.
// ---------------------------------------------------------------------------
function _formatSeconds(sec) {
  if (sec == null || sec === 0) return '0s';
  if (sec < 60) return `${Math.round(sec)}s`;
  const mins = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (mins < 60) return `${mins}m ${s}s`;
  const hrs = Math.floor(mins / 60);
  const m = mins % 60;
  return `${hrs}h ${m}m`;
}

function _formatInt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function _formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch (e) {
    return iso;
  }
}

function _formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
}


// ---------------------------------------------------------------------------
// useJson — thin fetch wrapper. Owns loading/error/data + retry(). Handles
// 403 (surfaces a friendlier message) and 404 (returns null-ish data).
// ---------------------------------------------------------------------------
function useJson(fetcher, deps) {
  const [state, setState] = useState({
    loading: true, error: null, data: null, forbidden: false, notFound: false,
  });
  const load = useCallback(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetcher().then(
      (res) => {
        if (cancelled) return;
        setState({
          loading: false, error: null, data: res.data?.data ?? res.data,
          forbidden: false, notFound: false,
        });
      },
      (err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setState({
          loading: false,
          error: err,
          data: null,
          forbidden: status === 403,
          notFound: status === 404,
        });
      },
    );
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => load(), [load]);
  return { ...state, reload: load };
}


// ---------------------------------------------------------------------------
// Ring gauge — pure SVG. `pct` in [0,100]. Renders active-session count as
// a big centered number with the gauge arc around it.
// ---------------------------------------------------------------------------
function RingGauge({ value, subtitle, pct }) {
  const clamped = Math.max(0, Math.min(100, pct ?? 0));
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);
  return (
    <div className="sa-analytics__ring">
      <svg viewBox="0 0 120 120" aria-hidden="true" width="120" height="120">
        <circle cx="60" cy="60" r={radius} fill="none"
                stroke="var(--st200)" strokeWidth="10" />
        <circle cx="60" cy="60" r={radius} fill="none"
                stroke="var(--g500)" strokeWidth="10"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 60 60)" />
      </svg>
      <div className="sa-analytics__ring-center">
        <div className="sa-analytics__ring-value">{_formatInt(value)}</div>
        <div className="sa-analytics__ring-sub">{subtitle}</div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// TinyBarChart — pure SVG. Draws a bar per data point across the full width.
// `data` = [{label, value}]. Labels shown below the bars, values in tooltips.
// ---------------------------------------------------------------------------
function TinyBarChart({ data, valueFormatter }) {
  if (!data || data.length === 0) {
    return <div className="sa-analytics__empty">No data in this window.</div>;
  }
  const max = Math.max(...data.map((d) => Number(d.value || 0)), 1);
  const width = 480;
  const height = 140;
  const barW = width / data.length - 3;
  const format = valueFormatter || _formatInt;
  return (
    <svg viewBox={`0 0 ${width} ${height + 30}`}
         className="sa-analytics__bars" aria-hidden="true"
         preserveAspectRatio="none">
      {data.map((d, i) => {
        const h = (Number(d.value || 0) / max) * height;
        return (
          <g key={`${d.label}-${i}`}>
            <rect
              x={i * (barW + 3)}
              y={height - h}
              width={barW}
              height={h}
              fill="var(--g500)"
              rx="2">
              <title>{`${d.label}: ${format(d.value)}`}</title>
            </rect>
            {i % Math.ceil(data.length / 8) === 0 && (
              <text x={i * (barW + 3) + barW / 2} y={height + 20}
                    textAnchor="middle"
                    fontSize="10"
                    fill="var(--st500)">
                {d.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}


// ---------------------------------------------------------------------------
// TallyList — the ranked "top X" lists used throughout Now.
// ---------------------------------------------------------------------------
function TallyList({ title, items, labelKey, countKey = 'session_count' }) {
  return (
    <div className="sa-analytics__tally">
      <h4>{title}</h4>
      {(!items || items.length === 0) ? (
        <div className="sa-analytics__empty">No data.</div>
      ) : (
        <ul>
          {items.map((it, i) => (
            <li key={`${it[labelKey] || 'unknown'}-${i}`}>
              <span className="sa-analytics__tally-label">{it[labelKey] || '(none)'}</span>
              <span className="sa-analytics__tally-count td-amt">{_formatInt(it[countKey])}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// NowView
// ---------------------------------------------------------------------------
function NowView({ adminApiPrefix }) {
  const [minutes, setMinutes] = useState(15);
  const { loading, error, data, reload } = useJson(
    () => api.adminAnalyticsNow(adminApiPrefix, minutes),
    [adminApiPrefix, minutes],
  );

  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  if (loading) {
    return <Loading variant="spinner" message="Loading current activity…" />;
  }
  if (error) {
    return (
      <div className="sa-analytics__error" role="alert">
        Could not load current activity.{' '}
        <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
      </div>
    );
  }
  const gaugePct = Math.min(100, (data.active_session_count / 20) * 100);
  return (
    <section aria-labelledby="sa-analytics-now-heading">
      <h3 id="sa-analytics-now-heading" ref={headingRef} tabIndex={-1}>
        Right now — last {data.window_minutes} minute{data.window_minutes === 1 ? '' : 's'}
      </h3>
      <div className="sa-analytics__controls">
        <label htmlFor="sa-analytics-minutes">Window (min):</label>
        <select
          id="sa-analytics-minutes"
          className="input"
          value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))}
        >
          <option value={5}>5</option>
          <option value={15}>15</option>
          <option value={30}>30</option>
          <option value={60}>60</option>
        </select>
      </div>

      <div className="sa-analytics__now-summary">
        <RingGauge
          value={data.active_session_count}
          subtitle="active sessions"
          pct={gaugePct}
        />
        <div className="sa-analytics__now-stat">
          <div className="sa-analytics__now-stat-label">Engaged time (total)</div>
          <div className="sa-analytics__now-stat-value td-amt">
            {_formatSeconds(data.engaged_seconds_total)}
          </div>
        </div>
      </div>

      <div className="sa-analytics__tallies">
        <TallyList title="Top pages" items={data.top_pages} labelKey="path" />
        <TallyList title="Top countries" items={data.top_countries} labelKey="geo_country" />
        <TallyList title="Top devices" items={data.top_devices} labelKey="device_type" />
        <TallyList title="Top browsers" items={data.top_browsers} labelKey="browser_family" />
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// TrendsView
// ---------------------------------------------------------------------------
function TrendsView({ adminApiPrefix }) {
  const [days, setDays] = useState(30);
  const { loading, error, data, reload } = useJson(
    () => api.adminAnalyticsTrends(adminApiPrefix, days),
    [adminApiPrefix, days],
  );

  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  const barsSessions = useMemo(() => (data?.series || []).map((d) => ({
    label: (d.day || '').slice(5),  // MM-DD only
    value: d.sessions,
  })), [data]);

  if (loading) return <Loading variant="spinner" message="Loading trends…" />;
  if (error) {
    return (
      <div className="sa-analytics__error" role="alert">
        Could not load trends.{' '}
        <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
      </div>
    );
  }
  const totals = data.totals || {};
  return (
    <section aria-labelledby="sa-analytics-trends-heading">
      <h3 id="sa-analytics-trends-heading" ref={headingRef} tabIndex={-1}>
        Trends — last {data.days} day{data.days === 1 ? '' : 's'}
      </h3>
      <div className="sa-analytics__controls">
        <label htmlFor="sa-analytics-days">Window (days):</label>
        <select
          id="sa-analytics-days"
          className="input"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={7}>7</option>
          <option value={30}>30</option>
          <option value={90}>90</option>
          <option value={180}>180</option>
          <option value={365}>365</option>
        </select>
      </div>

      <div className="sa-analytics__kpis">
        <div className="sa-analytics__kpi">
          <div className="sa-analytics__kpi-label">Sessions</div>
          <div className="sa-analytics__kpi-value td-amt">{_formatInt(totals.sessions)}</div>
        </div>
        <div className="sa-analytics__kpi">
          <div className="sa-analytics__kpi-label">Pageviews</div>
          <div className="sa-analytics__kpi-value td-amt">{_formatInt(totals.pageviews)}</div>
        </div>
        <div className="sa-analytics__kpi">
          <div className="sa-analytics__kpi-label">Engaged time</div>
          <div className="sa-analytics__kpi-value td-amt">{_formatSeconds(totals.engaged_seconds)}</div>
        </div>
        <div className="sa-analytics__kpi">
          <div className="sa-analytics__kpi-label">Bounce rate</div>
          <div className="sa-analytics__kpi-value td-amt">{totals.bounce_rate_pct ?? 0}%</div>
        </div>
      </div>

      <h4>Sessions per day</h4>
      <TinyBarChart data={barsSessions} />

      <h4>Detail</h4>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Sessions</th>
              <th scope="col">Pageviews</th>
              <th scope="col">Engaged</th>
              <th scope="col">Unique users</th>
              <th scope="col">Bounce %</th>
            </tr>
          </thead>
          <tbody>
            {(data.series || []).map((row) => (
              <tr key={row.day}>
                <td>{row.day}</td>
                <td><span className="td-amt">{_formatInt(row.sessions)}</span></td>
                <td><span className="td-amt">{_formatInt(row.pageviews)}</span></td>
                <td><span className="td-amt">{_formatSeconds(row.engaged_seconds)}</span></td>
                <td><span className="td-amt">{_formatInt(row.unique_users)}</span></td>
                <td><span className="td-amt">{row.bounce_rate_pct}%</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Session drill-down — shared by PeopleView and SessionsView.
//
// A session row expands in place (no modal, no drawer) into two stacked
// blocks: a facts grid answering "what device was this" and a timeline
// answering "what were they doing". Both come from the same
// GET .../analytics/sessions/<uuid> call, which is only fired once the row
// is actually expanded — a 50-row list must not issue 50 detail requests.
// ---------------------------------------------------------------------------

/**
 * Label/value grid describing the device, locale, and origin of one session.
 *
 * @param {Object} props
 * @param {Object} props.session - Session row from the sessions/<id> endpoint.
 * @returns {JSX.Element} A definition-list style facts grid.
 */
function SessionFacts({ session }) {
  const geo = [session.geo_city, session.geo_region, session.geo_country]
    .filter(Boolean)
    .join(', ');
  const facts = [
    { label: 'Browser', value: session.browser_family },
    { label: 'OS', value: session.os },
    { label: 'Device', value: session.device_type },
    { label: 'Screen', value: session.screen_size, mono: true },
    { label: 'Language', value: session.language, mono: true },
    { label: 'Location', value: geo },
    { label: 'Entry page', value: session.entry_path, mono: true },
    { label: 'Referrer', value: session.referrer || session.referrer_type },
    { label: 'Started', value: _formatDate(session.started_at) },
    { label: 'Last seen', value: _formatDate(session.last_seen_at) },
    { label: 'Engaged', value: _formatSeconds(session.engaged_seconds), mono: true },
    { label: 'Session ID', value: session.id, mono: true },
  ];
  return (
    <dl className="sa-analytics__facts">
      {facts.map((f) => (
        <div className="sa-analytics__fact" key={f.label}>
          <dt>{f.label}</dt>
          <dd className={f.mono ? 'td-amt' : undefined}>{f.value || '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Ordered breadcrumb of what happened during one session.
 *
 * Pageviews and custom events share the timeline; the event `type` is shown
 * as a badge so status is never communicated by position alone.
 *
 * @param {Object} props
 * @param {Array<Object>} props.events - Events, oldest first.
 * @param {number} props.eventCount - Total events recorded for the session.
 * @param {boolean} props.truncated - True when the server capped the list.
 * @returns {JSX.Element} The timeline, or an empty state.
 */
function SessionTimeline({ events, eventCount, truncated }) {
  if (!events || events.length === 0) {
    return (
      <div className="sa-analytics__empty">
        No events recorded for this session — the visit registered but no
        pageview beacon landed.
      </div>
    );
  }
  return (
    <>
      <ol className="sa-analytics__timeline">
        {events.map((e) => (
          <li className="sa-analytics__timeline-item" key={e.id}>
            <span className="sa-analytics__timeline-time td-amt">
              {_formatTime(e.occurred_at)}
            </span>
            <span
              className={`badge sa-analytics__timeline-kind sa-analytics__timeline-kind--${e.type}`}
            >
              {e.type}
            </span>
            <span className="sa-analytics__timeline-label">
              {e.name || e.path || e.title || '(unnamed)'}
            </span>
            {e.name && e.path && (
              <span className="sa-analytics__timeline-path td-amt">{e.path}</span>
            )}
          </li>
        ))}
      </ol>
      {truncated && (
        <p className="sa-analytics__timeline-note">
          Showing the first {events.length} of {_formatInt(eventCount)} events.
        </p>
      )}
    </>
  );
}

/**
 * Fetch-and-render panel for one expanded session row.
 *
 * Mounted only while the row is expanded, so unmounting cancels nothing but
 * also costs nothing — the next expand refetches, which is what an operator
 * watching a live session wants anyway.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Full admin API prefix.
 * @param {string} props.sessionId - Session UUID to load.
 * @returns {JSX.Element} Loading, error, or the facts + timeline panel.
 */
function SessionDetail({ adminApiPrefix, sessionId }) {
  const { loading, error, notFound, data, reload } = useJson(
    () => api.adminAnalyticsSession(adminApiPrefix, sessionId),
    [adminApiPrefix, sessionId],
  );

  if (loading) {
    return <Loading variant="spinner" message="Loading session…" />;
  }
  if (notFound) {
    return (
      <div className="sa-analytics__error" role="alert">
        This session is no longer available — it may have aged out of retention.
      </div>
    );
  }
  if (error) {
    return (
      <div className="sa-analytics__error" role="alert">
        Could not load this session.{' '}
        <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }
  if (!data || !data.session) return null;

  return (
    <div className="sa-analytics__session">
      <h6 className="sa-analytics__session-heading">Device &amp; origin</h6>
      <SessionFacts session={data.session} />
      <h6 className="sa-analytics__session-heading">
        Activity ({_formatInt(data.event_count)} event
        {data.event_count === 1 ? '' : 's'})
      </h6>
      <SessionTimeline
        events={data.events}
        eventCount={data.event_count}
        truncated={data.truncated}
      />
    </div>
  );
}

/**
 * The chevron button that expands/collapses a session row.
 *
 * A real <button> rather than a clickable <tr> so keyboard activation,
 * focus ring, and aria-expanded all come for free.
 *
 * @param {Object} props
 * @param {boolean} props.expanded - Current expansion state.
 * @param {string} props.panelId - id of the panel this button controls.
 * @param {string} props.label - Human description of the row, for the a11y name.
 * @param {Function} props.onToggle - Click handler.
 * @returns {JSX.Element} The toggle button.
 */
function SessionRowToggle({ expanded, panelId, label, onToggle }) {
  return (
    <button
      type="button"
      className="sa-analytics__row-toggle"
      aria-expanded={expanded}
      aria-controls={panelId}
      onClick={onToggle}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={`sa-analytics__chevron${expanded ? ' sa-analytics__chevron--open' : ''}`}
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
      <span className="sr-only">
        {expanded ? 'Hide' : 'Show'} session detail for {label}
      </span>
    </button>
  );
}

/**
 * Shared expand/collapse state for a table of session rows.
 *
 * @returns {{expandedId: string|null, toggle: Function, collapse: Function}}
 *   The currently expanded session id (only one at a time — two open
 *   timelines side by side is noise, not comparison), its toggle, and a
 *   collapse-all for when the surrounding list changes underneath it.
 */
function useExpandedSession() {
  const [expandedId, setExpandedId] = useState(null);
  const toggle = useCallback(
    (id) => setExpandedId((cur) => (cur === id ? null : id)),
    [],
  );
  const collapse = useCallback(() => setExpandedId(null), []);
  return { expandedId, toggle, collapse };
}


// ---------------------------------------------------------------------------
// PeopleView — list + drill-in detail panel.
// ---------------------------------------------------------------------------
function PeopleView({ adminApiPrefix }) {
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState(null);
  const list = useJson(
    () => api.adminAnalyticsPeople(adminApiPrefix, days, 50),
    [adminApiPrefix, days],
  );

  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  const detail = useJson(
    () => selected
      ? api.adminAnalyticsPerson(adminApiPrefix, selected)
      : Promise.resolve({ data: { data: null } }),
    [adminApiPrefix, selected],
  );

  // Which session row is expanded inside the person detail panel. Reset on
  // person change so a stale row id can't leave a phantom panel open.
  const { expandedId, toggle, collapse } = useExpandedSession();
  useEffect(() => { collapse(); }, [selected, collapse]);

  if (list.loading) return <Loading variant="spinner" message="Loading people…" />;
  if (list.error) {
    return (
      <div className="sa-analytics__error" role="alert">
        Could not load people.{' '}
        <button type="button" className="btn btn-sm btn-secondary" onClick={list.reload}>Retry</button>
      </div>
    );
  }
  const rows = list.data?.people || [];
  return (
    <section aria-labelledby="sa-analytics-people-heading">
      <h3 id="sa-analytics-people-heading" ref={headingRef} tabIndex={-1}>
        People — last {list.data.days} day{list.data.days === 1 ? '' : 's'}
      </h3>
      <div className="sa-analytics__controls">
        <label htmlFor="sa-analytics-people-days">Window (days):</label>
        <select
          id="sa-analytics-people-days"
          className="input"
          value={days}
          onChange={(e) => { setDays(Number(e.target.value)); setSelected(null); }}
        >
          <option value={7}>7</option>
          <option value={30}>30</option>
          <option value={90}>90</option>
        </select>
      </div>

      <p className="sa-analytics__hint">
        People only lists visitors we could resolve to a signed-in account.
        Signed-out traffic — the landing page, the signup funnel — lives under
        the <strong>Sessions</strong> tab.
      </p>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">User</th>
              <th scope="col">Email</th>
              <th scope="col">Sessions</th>
              <th scope="col">Engaged</th>
              <th scope="col">Last seen</th>
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="admin-cell-muted">No tracked users in this window.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.user_id}>
                <td>{r.name || '(no name)'}</td>
                <td>{r.email}</td>
                <td><span className="td-amt">{_formatInt(r.session_count)}</span></td>
                <td><span className="td-amt">{_formatSeconds(r.engaged_seconds)}</span></td>
                <td>{_formatDate(r.last_seen)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-xs btn-secondary"
                    onClick={() => setSelected(r.user_id)}
                    aria-label={`View detail for ${r.email}`}
                  >View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="sa-analytics__detail" role="region" aria-labelledby="sa-analytics-person-heading">
          <div className="sa-analytics__detail-head">
            <h4 id="sa-analytics-person-heading">Person detail</h4>
            <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => setSelected(null)}>Close</button>
          </div>
          {detail.loading && <Loading variant="spinner" message="Loading person…" />}
          {detail.notFound && (
            <div className="sa-analytics__error" role="alert">
              This user has been deactivated or removed.
            </div>
          )}
          {detail.error && !detail.notFound && (
            <div className="sa-analytics__error" role="alert">
              Could not load user detail. <button
                type="button" className="btn btn-sm btn-secondary" onClick={detail.reload}>Retry</button>
            </div>
          )}
          {detail.data && detail.data.traits && (
            <>
              <div className="sa-analytics__kpis">
                <div className="sa-analytics__kpi">
                  <div className="sa-analytics__kpi-label">Sessions (all time)</div>
                  <div className="sa-analytics__kpi-value td-amt">
                    {_formatInt(detail.data.traits.session_count)}
                  </div>
                </div>
                <div className="sa-analytics__kpi">
                  <div className="sa-analytics__kpi-label">Engaged (all time)</div>
                  <div className="sa-analytics__kpi-value td-amt">
                    {_formatSeconds(detail.data.traits.engaged_seconds_total)}
                  </div>
                </div>
                <div className="sa-analytics__kpi">
                  <div className="sa-analytics__kpi-label">Organizations</div>
                  <div className="sa-analytics__kpi-value td-amt">
                    {_formatInt(detail.data.traits.organization_count)}
                  </div>
                </div>
              </div>

              <h5>Recent sessions</h5>
              <p className="sa-analytics__hint">
                Expand a session to see the device it was on and the trail of
                pages and actions during that visit.
              </p>
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col" aria-label="Expand" />
                      <th scope="col">Started</th>
                      <th scope="col">Entry</th>
                      <th scope="col">Device</th>
                      <th scope="col">Country</th>
                      <th scope="col">Engaged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.recent_sessions.length === 0 && (
                      <tr><td colSpan={6} className="admin-cell-muted">No sessions.</td></tr>
                    )}
                    {detail.data.recent_sessions.map((s) => {
                      const isOpen = expandedId === s.id;
                      const panelId = `sa-person-session-${s.id}`;
                      return (
                        <React.Fragment key={s.id}>
                          <tr className={isOpen ? 'sa-analytics__row--open' : undefined}>
                            <td>
                              <SessionRowToggle
                                expanded={isOpen}
                                panelId={panelId}
                                label={_formatDate(s.started_at)}
                                onToggle={() => toggle(s.id)}
                              />
                            </td>
                            <td>{_formatDate(s.started_at)}</td>
                            <td>{s.entry_path || '(none)'}</td>
                            <td>{s.device_type || '—'} / {s.browser_family || '—'}</td>
                            <td>{s.geo_country || '—'}</td>
                            <td><span className="td-amt">{_formatSeconds(s.engaged_seconds)}</span></td>
                          </tr>
                          {isOpen && (
                            <tr className="sa-analytics__row-detail">
                              <td colSpan={6} id={panelId}>
                                <SessionDetail
                                  adminApiPrefix={adminApiPrefix}
                                  sessionId={s.id}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// SessionsView — every visit, anonymous included.
//
// The reason this view exists: `get_people` JOINs `users` on
// `analytics_sessions.user_id`, so a signed-out visit can never appear there.
// Early on, that is almost all of the traffic — which is why the console read
// as "we aren't tracking anything" when in fact rows were landing fine.
// ---------------------------------------------------------------------------
function SessionsView({ adminApiPrefix }) {
  const [days, setDays] = useState(30);
  const [identified, setIdentified] = useState('all');
  const { expandedId, toggle, collapse } = useExpandedSession();

  const list = useJson(
    () => api.adminAnalyticsSessions(adminApiPrefix, days, 100, identified),
    [adminApiPrefix, days, identified],
  );

  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);
  // Filters rewrite the row set — an id that survived the change would point
  // at a row that is no longer on screen.
  useEffect(() => { collapse(); }, [days, identified, collapse]);

  if (list.loading) return <Loading variant="spinner" message="Loading sessions…" />;
  if (list.error) {
    return (
      <div className="sa-analytics__error" role="alert">
        Could not load sessions.{' '}
        <button type="button" className="btn btn-sm btn-secondary" onClick={list.reload}>
          Retry
        </button>
      </div>
    );
  }

  const rows = list.data?.sessions || [];
  return (
    <section aria-labelledby="sa-analytics-sessions-heading">
      <h3 id="sa-analytics-sessions-heading" ref={headingRef} tabIndex={-1}>
        Sessions — last {list.data.days} day{list.data.days === 1 ? '' : 's'}
      </h3>
      <div className="sa-analytics__controls">
        <label htmlFor="sa-analytics-sessions-days">Window (days):</label>
        <select
          id="sa-analytics-sessions-days"
          className="input"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={1}>1</option>
          <option value={7}>7</option>
          <option value={30}>30</option>
          <option value={90}>90</option>
        </select>

        <label htmlFor="sa-analytics-sessions-identified">Visitors:</label>
        <select
          id="sa-analytics-sessions-identified"
          className="input"
          value={identified}
          onChange={(e) => setIdentified(e.target.value)}
        >
          <option value="all">All</option>
          <option value="yes">Signed in</option>
          <option value="no">Anonymous</option>
        </select>
      </div>

      <p className="sa-analytics__hint">
        Expand a session to see the device it came from and the trail of pages
        and actions during that visit.
      </p>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col" aria-label="Expand" />
              <th scope="col">Started</th>
              <th scope="col">Visitor</th>
              <th scope="col">Device</th>
              <th scope="col">Entry</th>
              <th scope="col">Location</th>
              <th scope="col">Engaged</th>
              <th scope="col">Events</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="admin-cell-muted">
                  No sessions recorded in this window. First-party tracking
                  started 2026-08-24 — nothing before that date was captured.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const isOpen = expandedId === r.id;
              const panelId = `sa-session-${r.id}`;
              const who = r.email || r.name || 'Anonymous';
              return (
                <React.Fragment key={r.id}>
                  <tr className={isOpen ? 'sa-analytics__row--open' : undefined}>
                    <td>
                      <SessionRowToggle
                        expanded={isOpen}
                        panelId={panelId}
                        label={`${who} at ${_formatDate(r.started_at)}`}
                        onToggle={() => toggle(r.id)}
                      />
                    </td>
                    <td>{_formatDate(r.started_at)}</td>
                    <td>
                      {r.user_id ? (
                        <span>{r.email || r.name}</span>
                      ) : (
                        <span className="badge sa-analytics__badge--anon">Anonymous</span>
                      )}
                    </td>
                    <td>{r.device_type || '—'} / {r.browser_family || '—'}</td>
                    <td>{r.entry_path || '(none)'}</td>
                    <td>
                      {[r.geo_city, r.geo_country].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td><span className="td-amt">{_formatSeconds(r.engaged_seconds)}</span></td>
                    <td><span className="td-amt">{_formatInt(r.event_count)}</span></td>
                  </tr>
                  {isOpen && (
                    <tr className="sa-analytics__row-detail">
                      <td colSpan={8} id={panelId}>
                        <SessionDetail
                          adminApiPrefix={adminApiPrefix}
                          sessionId={r.id}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Top-level shell — segmented control + view switcher.
// ---------------------------------------------------------------------------
export default function SuperAdminAnalytics() {
  const { adminApiPrefix } = useAuth();
  const [view, setView] = useState('now');

  if (!adminApiPrefix) {
    return <div className="sa-analytics"><Loading variant="spinner" message="Loading…" /></div>;
  }

  return (
    <div className="sa-analytics">
      <div className="sa-analytics__header">
        <h2>Analytics</h2>
        <div role="tablist" aria-label="Analytics view" className="sa-analytics__tabs">
          {[
            { id: 'now', label: 'Now' },
            { id: 'trends', label: 'Trends' },
            { id: 'people', label: 'People' },
            { id: 'sessions', label: 'Sessions' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={view === t.id}
              tabIndex={view === t.id ? 0 : -1}
              className={`sa-analytics__tab ${view === t.id ? 'sa-analytics__tab--active' : ''}`}
              onClick={() => setView(t.id)}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <div className="sa-analytics__body">
        {view === 'now' && <NowView adminApiPrefix={adminApiPrefix} />}
        {view === 'trends' && <TrendsView adminApiPrefix={adminApiPrefix} />}
        {view === 'people' && <PeopleView adminApiPrefix={adminApiPrefix} />}
        {view === 'sessions' && <SessionsView adminApiPrefix={adminApiPrefix} />}
      </div>
    </div>
  );
}
